/**
 * Selection state with shift-click range support for list-style tables.
 *
 * The pure `computeRangeToggle` function holds the logic so it can be unit
 * tested without React. The `useRangeSelection` hook is a thin stateful
 * wrapper that tracks `selectedIds` and an "anchor" — the last row clicked
 * without shift, used as the pivot for subsequent shift-clicks.
 *
 * Shift-click semantics match Excel / Windows Explorer:
 *   - Regular click: toggles the row, sets it as the new anchor.
 *   - Shift-click: applies the *inverse* of the target's current state to
 *     the entire anchor→target range. If the target was unselected, the
 *     range becomes selected; if it was selected, the range becomes
 *     deselected.
 *   - Shift-click does NOT move the anchor — users can pivot from the
 *     same anchor repeatedly to adjust the range.
 *   - With no prior anchor, shift-click degrades to a single toggle.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface ToggleOptions {
  shiftKey?: boolean;
}

export interface RangeToggleInput {
  selectedIds: ReadonlySet<string>;
  /** Visible items in display order — only ids are needed for range math. */
  itemIds: readonly string[];
  /**
   * Optional `id → index` map matching `itemIds`. The hook passes this in to
   * avoid two O(n) `indexOf` scans per shift-click; tests and one-off callers
   * can omit it and we'll scan.
   */
  itemIndexById?: ReadonlyMap<string, number>;
  /** The row being clicked. */
  toggleId: string;
  shiftKey: boolean;
  /** The previous anchor: the last row clicked without shift, or null. */
  anchorId: string | null;
}

export interface RangeToggleResult {
  selectedIds: Set<string>;
  anchorId: string | null;
}

export function computeRangeToggle({
  selectedIds,
  itemIds,
  itemIndexById,
  toggleId,
  shiftKey,
  anchorId,
}: RangeToggleInput): RangeToggleResult {
  if (shiftKey && anchorId !== null) {
    // When the caller passes an itemIndexById map, trust it as authoritative
    // (Map.get returns undefined for "not found", which we fold to -1);
    // otherwise fall back to a linear scan so tests and one-off callers
    // can skip building a map.
    const anchorIdx = itemIndexById
      ? (itemIndexById.get(anchorId) ?? -1)
      : itemIds.indexOf(anchorId);
    const targetIdx = itemIndexById
      ? (itemIndexById.get(toggleId) ?? -1)
      : itemIds.indexOf(toggleId);
    if (anchorIdx !== -1 && targetIdx !== -1) {
      const [lo, hi] = anchorIdx <= targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];
      const shouldSelect = !selectedIds.has(toggleId);
      const next = new Set(selectedIds);
      for (let i = lo; i <= hi; i++) {
        const rid = itemIds[i];
        if (shouldSelect) next.add(rid);
        else next.delete(rid);
      }
      return { selectedIds: next, anchorId };
    }
    // Anchor or target fell out of the visible list — fall through to
    // single-toggle so the click still does something sensible.
  }

  const next = new Set(selectedIds);
  if (next.has(toggleId)) next.delete(toggleId);
  else next.add(toggleId);
  return { selectedIds: next, anchorId: toggleId };
}

/**
 * Items must carry a stable `id: string`. We intentionally don't take a
 * `getId` callback — inline callers would change its identity every render
 * and defeat the `itemIds` memo below, which is on the render-critical path.
 */
export interface UseRangeSelectionOptions<T extends { id: string }> {
  items: readonly T[];
}

export interface UseRangeSelectionResult {
  selectedIds: ReadonlySet<string>;
  toggle: (id: string, opts?: ToggleOptions) => void;
  clear: () => void;
}

export function useRangeSelection<T extends { id: string }>({
  items,
}: UseRangeSelectionOptions<T>): UseRangeSelectionResult {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const anchorIdRef = useRef<string | null>(null);

  const itemIds = useMemo(() => items.map((item) => item.id), [items]);

  // Map for O(1) anchor/target lookup in shift-click range resolution.
  // Recomputed alongside itemIds so it stays in sync.
  const itemIndexById = useMemo(() => {
    const map = new Map<string, number>();
    itemIds.forEach((id, i) => map.set(id, i));
    return map;
  }, [itemIds]);

  // Prune selection (and anchor) to the current item set. Without this, rows
  // that get filtered out of the visible list stay in `selectedIds` — which
  // silently over-reports the bulk-action count and can apply mutations to
  // rows the user can no longer see.
  useEffect(() => {
    const itemIdSet = new Set(itemIds);
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (itemIdSet.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
    if (anchorIdRef.current !== null && !itemIdSet.has(anchorIdRef.current)) {
      anchorIdRef.current = null;
    }
  }, [itemIds]);

  const toggle = useCallback(
    (id: string, opts?: ToggleOptions) => {
      setSelectedIds((prev) => {
        const result = computeRangeToggle({
          selectedIds: prev,
          itemIds,
          itemIndexById,
          toggleId: id,
          shiftKey: opts?.shiftKey === true,
          anchorId: anchorIdRef.current,
        });
        anchorIdRef.current = result.anchorId;
        return result.selectedIds;
      });
    },
    [itemIds, itemIndexById]
  );

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    anchorIdRef.current = null;
  }, []);

  return {
    selectedIds,
    toggle,
    clear,
  };
}
